import React, { useMemo, useState } from 'react';
import { getCastellerFullName } from '../../../utils/castellerName';
import { memberFromCasteller, memberFullName, searchCastellers, suggestedCastellers } from './legalBoardUi';

/** Local rows for the editor: the term members plus a stable React key. */
export const termMembersToLocal = (members) => (members || []).map((member, index) => ({
    key: `m-${member.id ?? index}-${index}`,
    castellerId: member.castellerId ?? null,
    legalFirstName: member.legalFirstName || '',
    legalLastName: member.legalLastName || '',
    roleLabel: member.roleLabel || '',
}));

export const errorMessages = (errors) => (Array.isArray(errors) ? errors.map((error) => error.message).filter(Boolean) : []);

/**
 * Who forms the board: a search over castellersInfo, people without an Aleta
 * user, and a plain Nom · Cognoms · Càrrec table with the fields in the cells.
 * Shared by the wizard (new board) and the correction form (current board).
 */
function LegalBoardCompositionEditor({
    members,
    onMembersChange,
    exceptionNote,
    onExceptionChange,
    castellersInfo,
    roleSuggestions = [],
    validation,
    serverErrors = [],
    disabled = false,
    busy = false,
}) {
    const [search, setSearch] = useState('');
    const [showExternalForm, setShowExternalForm] = useState(false);
    const [external, setExternal] = useState({ legalFirstName: '', legalLastName: '', roleLabel: '' });
    const [externalError, setExternalError] = useState(null);
    const [exceptionOpen, setExceptionOpen] = useState(Boolean(exceptionNote));

    const editable = !disabled;
    const excludedIds = useMemo(() => members.map((member) => member.castellerId).filter(Boolean), [members]);
    const searchResults = useMemo(() => searchCastellers(castellersInfo, search, { exclude: excludedIds }), [castellersInfo, search, excludedIds]);
    const suggestions = useMemo(() => suggestedCastellers(castellersInfo, { exclude: excludedIds }), [castellersInfo, excludedIds]);
    const missingRequiredRole = (validation?.errors || []).some((item) => item.code === 'MISSING_PRESIDENT' || item.code === 'MISSING_SECRETARY');
    const visibleErrors = members.length > 0 ? errorMessages((validation?.errors || []).filter((item) => item.code !== 'NO_MEMBERS')) : [];
    const warnings = validation?.warnings || [];

    const update = (next) => onMembersChange(next);

    const addCasteller = (casteller) => {
        update([...members, { ...memberFromCasteller(casteller), key: `c-${casteller.id}-${Date.now()}` }]);
        setSearch('');
    };

    const addExternal = () => {
        const first = external.legalFirstName.trim();
        const last = external.legalLastName.trim();
        if (!first || !last) {
            setExternalError('Cal indicar nom i cognoms tal com consten al DNI.');
            return;
        }
        update([...members, { key: `x-${Date.now()}`, castellerId: null, legalFirstName: first, legalLastName: last, roleLabel: external.roleLabel.trim() }]);
        setExternal({ legalFirstName: '', legalLastName: '', roleLabel: '' });
        setExternalError(null);
        setShowExternalForm(false);
    };

    const updateMember = (key, patch) => update(members.map((member) => (member.key === key ? { ...member, ...patch } : member)));

    const moveMember = (key, direction) => {
        const index = members.findIndex((member) => member.key === key);
        const target = index + direction;
        if (index === -1 || target < 0 || target >= members.length) return;
        const next = [...members];
        [next[index], next[target]] = [next[target], next[index]];
        update(next);
    };

    const removeMember = (key) => update(members.filter((member) => member.key !== key));

    const renderTable = () => (
        <div className="legal-board-composition-wrap">
            <table className="legal-board-composition-table" aria-label="Composició de la junta">
                <thead>
                    <tr>
                        <th scope="col">Nom</th>
                        <th scope="col">Cognoms</th>
                        <th scope="col">Càrrec</th>
                        {editable ? <th scope="col"><span className="legal-board-sr-only">Accions</span></th> : null}
                    </tr>
                </thead>
                <tbody>
                    {members.map((member, index) => {
                        const name = memberFullName(member);
                        return (
                            <tr key={member.key}>
                                <td data-label="Nom">
                                    {editable ? (
                                        <input type="text" aria-label="Nom" value={member.legalFirstName} onChange={(event) => updateMember(member.key, { legalFirstName: event.target.value })} disabled={busy} />
                                    ) : member.legalFirstName}
                                </td>
                                <td data-label="Cognoms">
                                    {editable ? (
                                        <input type="text" aria-label="Cognoms" value={member.legalLastName} onChange={(event) => updateMember(member.key, { legalLastName: event.target.value })} disabled={busy} />
                                    ) : member.legalLastName}
                                </td>
                                <td data-label="Càrrec">
                                    {editable ? (
                                        <input
                                            type="text"
                                            list="legal-board-roles"
                                            aria-label="Càrrec"
                                            placeholder="Presidència, Secretaria…"
                                            value={member.roleLabel}
                                            onChange={(event) => updateMember(member.key, { roleLabel: event.target.value })}
                                            disabled={busy}
                                        />
                                    ) : (member.roleLabel || 'Sense càrrec')}
                                </td>
                                {editable ? (
                                    <td data-label="" className="legal-board-composition-actions">
                                        <button type="button" className="legal-board-icon-button" onClick={() => moveMember(member.key, -1)} disabled={busy || index === 0} aria-label={`Puja ${name}`} title="Puja"><i className="fas fa-arrow-up" aria-hidden="true"></i></button>
                                        <button type="button" className="legal-board-icon-button" onClick={() => moveMember(member.key, 1)} disabled={busy || index === members.length - 1} aria-label={`Baixa ${name}`} title="Baixa"><i className="fas fa-arrow-down" aria-hidden="true"></i></button>
                                        <button type="button" className="legal-board-icon-button danger" onClick={() => removeMember(member.key)} disabled={busy} aria-label={`Treu ${name}`} title="Treu de la junta"><i className="fas fa-times" aria-hidden="true"></i></button>
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="legal-board-composition">
            {editable ? (
                <div className="legal-board-field legal-board-search">
                    <label htmlFor="legal-board-member-search">Busca un casteller (nom, cognoms o sobrenom)</label>
                    <input id="legal-board-member-search" type="text" inputMode="search" autoComplete="off" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Escriu com a mínim dues lletres" disabled={busy} />
                    {search.trim().length >= 2 ? (
                        <ul className="legal-board-search-results" aria-live="polite">
                            {searchResults.length === 0 ? <li className="legal-board-muted legal-board-search-empty">Cap resultat.</li> : null}
                            {searchResults.map((casteller) => (
                                <li key={casteller.id}>
                                    <button type="button" className="legal-board-search-result" onClick={() => addCasteller(casteller)} disabled={busy}>
                                        <span>Afegir {getCastellerFullName(casteller, 'Sense nom', { includeSecondSurname: true })}{casteller.mote ? ` (${casteller.mote})` : ''}</span>
                                        {Number(casteller.hidden) === 1 ? <span className="legal-board-member-source">ocult</span> : null}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {!showExternalForm ? (
                        <button type="button" className="legal-board-button link small" onClick={() => setShowExternalForm(true)} disabled={busy}>
                            Afegir una persona sense usuari d&apos;Aleta
                        </button>
                    ) : null}
                </div>
            ) : null}

            {editable && suggestions.length > 0 ? (
                <div className="legal-board-suggestions" role="group" aria-label="Suggeriments">
                    <span className="legal-board-suggestions-label">Suggeriments (junta a Aleta):</span>
                    {suggestions.map((casteller) => (
                        <button key={casteller.id} type="button" className="legal-board-chip" onClick={() => addCasteller(casteller)} disabled={busy}>
                            <i className="fas fa-plus" aria-hidden="true"></i> {getCastellerFullName(casteller, 'Sense nom', { includeSecondSurname: true })}
                        </button>
                    ))}
                </div>
            ) : null}

            {showExternalForm && editable ? (
                <div className="legal-board-inline-form" role="group" aria-label="Persona sense usuari d'Aleta">
                    <div className="legal-board-form-grid three">
                        <div className="legal-board-field">
                            <label htmlFor="legal-board-external-first">Nom</label>
                            <input id="legal-board-external-first" type="text" value={external.legalFirstName} onChange={(event) => setExternal((value) => ({ ...value, legalFirstName: event.target.value }))} />
                        </div>
                        <div className="legal-board-field">
                            <label htmlFor="legal-board-external-last">Cognoms</label>
                            <input id="legal-board-external-last" type="text" value={external.legalLastName} onChange={(event) => setExternal((value) => ({ ...value, legalLastName: event.target.value }))} />
                        </div>
                        <div className="legal-board-field">
                            <label htmlFor="legal-board-external-role">Càrrec</label>
                            <input id="legal-board-external-role" type="text" list="legal-board-roles" value={external.roleLabel} onChange={(event) => setExternal((value) => ({ ...value, roleLabel: event.target.value }))} />
                        </div>
                    </div>
                    {externalError ? <p className="legal-board-field-error" role="alert">{externalError}</p> : null}
                    <div className="legal-board-inline-form-actions">
                        <button type="button" className="legal-board-button primary" onClick={addExternal}>Afegir a la junta</button>
                        <button type="button" className="legal-board-button link" onClick={() => { setShowExternalForm(false); setExternalError(null); }}>Cancel·lar</button>
                    </div>
                </div>
            ) : null}

            <datalist id="legal-board-roles">
                {roleSuggestions.map((role) => <option key={role} value={role} />)}
            </datalist>

            {members.length === 0 ? <p className="legal-board-muted">Encara no has afegit cap persona.</p> : renderTable()}

            {visibleErrors.length > 0 ? <p className="legal-board-field-warning" role="status">{visibleErrors.join(' · ')}</p> : null}
            {warnings.length > 0 ? <p className="legal-board-muted legal-board-small">{warnings.map((item) => item.message).join(' · ')}</p> : null}
            {editable && missingRequiredRole && !exceptionOpen ? (
                <button type="button" className="legal-board-button link small" onClick={() => setExceptionOpen(true)}>Els estatuts no preveuen algun d&apos;aquests càrrecs?</button>
            ) : null}
            {editable && exceptionOpen ? (
                <div className="legal-board-field">
                    <label htmlFor="legal-board-exception">Excepció estatutària</label>
                    <small id="legal-board-exception-help">
                        Aleta demana presidència i secretaria perquè són els càrrecs que signen el certificat oficial de renovació.
                        Si els estatuts de la colla organitzen la junta d&apos;una altra manera, explica-ho aquí: podràs confirmar la junta igualment i la nota quedarà a l&apos;historial.
                    </small>
                    <textarea id="legal-board-exception" aria-describedby="legal-board-exception-help" rows={2} value={exceptionNote} onChange={(event) => onExceptionChange(event.target.value)} disabled={busy} placeholder="Exemple: els estatuts preveuen una coordinació col·legiada sense presidència." />
                </div>
            ) : null}
            {serverErrors.length > 0 ? <p className="legal-board-field-error" role="alert">{serverErrors.join(' · ')}</p> : null}
        </div>
    );
}

export default LegalBoardCompositionEditor;
